import React from 'react';
import { useHistory } from 'react-router';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { PasswordFormValues, PasswordFormViolation } from 'types/state';
import { useServices } from 'hooks/useServices';
import { PassworMatchLoginError } from 'utils/errors';
import { useRouteMatchStrict } from 'hooks/useRouteMatchStrict';
import {
  isPasswordFormPasswordRepeatViolation,
  isPasswordFormPasswordViolation,
  validatePasswordFormValues,
} from 'utils/createPasswordFormValidator';
import { useSignedInFull } from 'hooks/useSignedIn';
import { ServicePageLayoutComponent } from 'design/servicePageLayout';
import { TextComponent } from 'design/text/textComponents';
import { FormControlComponent } from 'design/formControl';
import { PressControlComponent } from 'design/pressControl';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { FloatingLabelComponent } from 'design/floatingLabel';
import { TextInputControlComponent } from 'design/textInputControl';
import { LinkControlComponent } from 'design/linkControl';
import { INPUT_PASSWORD_MAX_LENGTH, ORIGIN_SITE_URL } from 'utils/constants';
import { useAppDispatcher } from 'hooks/useAppDispatcher';
import { LogoComponent } from 'design/logo';
import { ContentLayoutComponent } from 'design/contentLayout';
import { MarginComponent } from 'design/margin';
import { FieldsetLayoutComponent } from 'design/fieldsetLayout';

type MatchParams = {
  token: string;
};

const initialValues: PasswordFormValues = {
  password: '',
  passwordRepeat: '',
};

export const ClientPasswordEditor = () => {
  const {
    params: { token: emailToken = '' },
  } = useRouteMatchStrict<MatchParams>();

  const [values, setValues] = React.useState<PasswordFormValues>(initialValues);

  const [violations, setViolations] = React.useState<PasswordFormViolation[]>(
    []
  );

  const dispatch = useAppDispatcher();

  const { active, injectPromise } = useAsyncBoundary();

  const history = useHistory();

  const signedIn = useSignedInFull();

  const intlDictionary = useIntlDictionary();

  const { clientPasswordEditor: clientPasswordEditorIntl } = intlDictionary;

  const { setClientPassword: setClientPasswordService } = useServices();

  const handlePasswordChange = React.useCallback((nextValue) => {
    setValues((prev) => ({
      ...prev,
      password: nextValue,
    }));

    setViolations((violations) =>
      violations.filter(
        (violation) => !isPasswordFormPasswordViolation(violation)
      )
    );
  }, []);

  const handlePasswordRepeatChange = React.useCallback((nextValue) => {
    setValues((prev) => ({
      ...prev,
      passwordRepeat: nextValue,
    }));

    setViolations((violations) =>
      violations.filter(
        (violation) => !isPasswordFormPasswordRepeatViolation(violation)
      )
    );
  }, []);

  React.useLayoutEffect(() => {
    if (signedIn) {
      history.push('/');
    }
  }, [history, signedIn]);

  const handleSubmitError = React.useCallback((error) => {
    if (error instanceof PassworMatchLoginError) {
      setViolations([{ name: 'PasswordMatchedLogin' }]);

      setValues((prev) => ({
        ...prev,
        passwordRepeat: '',
      }));
    } else {
      throw error;
    }
  }, []);

  const handleSubmitSuccess = React.useCallback(() => {
    dispatch({
      name: 'ClientPasswordEditor__PasswordDidUpdate',
    });
  }, [dispatch]);

  const submit = React.useCallback(() => {
    setViolations([]);

    const violations = validatePasswordFormValues(values);

    if (violations.length > 0) {
      Promise.resolve(violations).then(setViolations);
    } else {
      const promise = setClientPasswordService({
        emailToken,
        password: values.password,
      })
        .then(handleSubmitSuccess)
        .catch(handleSubmitError);

      injectPromise(promise, { shouldShowFallback: false });
    }
  }, [
    emailToken,
    handleSubmitError,
    handleSubmitSuccess,
    injectPromise,
    setClientPasswordService,
    values,
  ]);

  const passwordHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'PasswordMissed':
          return intlDictionary.inputValidationRequired;
        case 'PasswordTooShort':
          return intlDictionary.inputValidationPassswordLength;
        case 'PasswordMatchedLogin':
          return intlDictionary.inputValidationMatchWithEmail;
      }
    }
  }, [intlDictionary, violations]);

  const passwordRepeatHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'PasswordRepeatMissed':
          return intlDictionary.inputValidationRequired;
        case 'PasswordsDidNotMatch':
          return intlDictionary.inputValidationPasswordsMatch;
      }
    }
  }, [intlDictionary, violations]);

  const passwordIsInvalid = React.useMemo(() => {
    return violations.some(isPasswordFormPasswordViolation);
  }, [violations]);

  const passwordRepeatIsInvalid = React.useMemo(() => {
    return violations.some(isPasswordFormPasswordRepeatViolation);
  }, [violations]);

  return (
    <ServicePageLayoutComponent
      logo={
        <LinkControlComponent to={ORIGIN_SITE_URL}>
          <TextComponent size="l">
            <LogoComponent />
          </TextComponent>
        </LinkControlComponent>
      }
      bodyContent={
        <FormControlComponent
          shouldFocusFirstInvalidInput={violations.length > 0}
          onSubmit={submit}
        >
          <ContentLayoutComponent
            title={
              <TextComponent size="l" family="futura">
                {clientPasswordEditorIntl.titleText}
              </TextComponent>
            }
            body={
              <>
                <TextComponent>
                  {clientPasswordEditorIntl.descriptionText}{' '}
                </TextComponent>
                <MarginComponent top={28} />
                <FieldsetLayoutComponent>
                  <FloatingLabelComponent
                    lifted={!!values.password}
                    titleText={
                      <TextComponent color="secondary" size="m">
                        {intlDictionary.passwordInputPlaceholder}
                      </TextComponent>
                    }
                    hintText={
                      <TextComponent color="critical" size="s">
                        {passwordHint}
                      </TextComponent>
                    }
                  >
                    <TextInputControlComponent
                      type="password"
                      name="password"
                      autoFocus
                      value={values.password}
                      maxLength={INPUT_PASSWORD_MAX_LENGTH}
                      disabled={active}
                      invalid={passwordIsInvalid}
                      onValueChange={handlePasswordChange}
                    />
                  </FloatingLabelComponent>
                  <FloatingLabelComponent
                    lifted={!!values.passwordRepeat}
                    invalid={passwordRepeatIsInvalid}
                    titleText={
                      <TextComponent color="secondary" size="m">
                        {intlDictionary.repeatPasswordInputPlaceholder}
                      </TextComponent>
                    }
                    hintText={
                      <TextComponent color="critical" size="s">
                        {passwordRepeatHint}
                      </TextComponent>
                    }
                  >
                    <TextInputControlComponent
                      type="password"
                      name="passwordRepeat"
                      value={values.passwordRepeat}
                      disabled={active}
                      invalid={passwordRepeatIsInvalid}
                      maxLength={INPUT_PASSWORD_MAX_LENGTH}
                      onValueChange={handlePasswordRepeatChange}
                    />
                  </FloatingLabelComponent>
                </FieldsetLayoutComponent>
                <MarginComponent top={48} />
                <PressControlComponent
                  disabled={active}
                  theme="primary"
                  type="submit"
                  hasSpinner={active}
                >
                  {clientPasswordEditorIntl.actionText}
                </PressControlComponent>
              </>
            }
            backAction={null}
          />
        </FormControlComponent>
      }
    />
  );
};
